import { FC } from 'react'
import styled, { css } from 'styled-components'
import Dropzone from 'react-dropzone'
import {
	IconComponent,
	LinkComponent,
	themeColor,
	themeFontFamily,
	ContentBlockComponent,
} from 'LibNeo'
import { PopoverOverlayStyled } from 'LibNeo/components/Basic/Popover/styled'
import { LinkComponentStyled } from 'LibNeo/components/Basic/Link/styled'
import { DropzoneOptions, FileStatuses, DropzoneSizeProps, DropzoneWrapperProps } from 'Modules'

export const CloseIcon = styled(IconComponent)``

export const Icon = styled(IconComponent)``

export const FilesWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	max-width: 400px;
	width: auto;
	z-index: 1;
`

export const UploadContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	font-family: ${themeFontFamily('SBSansText')};
	will-change: transform;
`

export const UploadPopoverOverlayStyled = styled(PopoverOverlayStyled)`
	.ant-popover-arrow {
		display: none;
	}
	.ant-popover-inner {
		margin-top: -26px;
		padding: 0;
		background: none;
		border: none;
		box-shadow: none;
	}
`

export const FileWrapper = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	padding: 5px 30px;
	width: 100%;
	background-color: ${themeColor('white')};
	border: 1px solid ${themeColor('gray', 3)};
	border-radius: 4px;
	${CloseIcon} {
		position: absolute;
		right: 6px;
		top: 6px;
	}
	${Icon} {
		position: absolute;
		left: 6px;
		top: 6px;
	}
`

export const FileNameWrapper = styled.div`
	white-space: nowrap;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	line-height: 20px;
`

export const FileInfo = styled.div<{ $status: FileStatuses }>`
	font-size: 12px;
	line-height: 20px;
	text-decoration: none;
	color: ${({ $status }) => {
		switch ($status) {
			case FileStatuses.WARNING:
				return themeColor('gold', 5)
			case FileStatuses.ERROR:
				return themeColor('pink', 3)
			default:
				return themeColor('gray', 5)
		}
	}};
`

export const FileLink = styled(LinkComponent)`
	font-size: 12px;
	line-height: 20px;
	text-decoration: none;
`

export const DropzoneArea = styled.div<
	{ dragEnter: boolean; disabled?: boolean; error?: boolean } & DropzoneSizeProps
>`
	display: flex;
	flex-direction: column;
	position: relative;
	justify-content: center;
	width: ${({ width }) => (width ? '100%' : 'fit-content')};
	height: ${({ height }) => height || '168px'};
	align-items: center;
	background-color: ${themeColor('white')};
	box-sizing: border-box;
	padding: 24px 40px;
	border-radius: 6px;
	cursor: pointer;
	line-height: 20px;
	font-size: 14px;
	color: ${themeColor('gray', 8)};

	&:before {
		position: absolute;
		top: 0;
		left: 0;
		content: '';
		width: 100%;
		height: 100%;
		border: 1px dashed ${themeColor('gray', 3)};
		border-radius: 6px;
	}

	&:hover {
		&:before {
			border: 2px dashed ${themeColor('emerald', 5)};
		}
	}

	&:focus {
		&:before {
			border: 2px dashed ${themeColor('emerald', 5)};
		}
	}

	${({ dragEnter }) =>
		dragEnter &&
		css`
			&:before {
				border: 2px dashed ${themeColor('emerald', 5)};
			}
		`}

	${({ disabled }) =>
		disabled &&
		css`
			background-color: ${themeColor('gray', 1)};
			pointer-events: none;
			cursor: default;

			&:before {
				border: none;
			}
		`}
	
	${({ error }) =>
		error &&
		css`
			&:before {
				border: 2px dashed ${themeColor('pink', 5)};
			}
		`}
`

export const DropzoneWrapper = styled.div<DropzoneWrapperProps>`
	width: ${({ width }) => width || 'fit-content'};
	display: flex;
	flex-direction: ${({ errorPlace }) => {
		switch (errorPlace) {
			case 'top':
				return 'column-reverse'
			case 'left':
				return 'row'
			case 'bottom':
				return 'column'
			case 'right':
				return 'row-reverse'
			default:
				return 'row'
		}
	}};
	align-items: ${({ errorPlace }) => {
		switch (errorPlace) {
			case 'top':
				return 'flex-start'
			case 'left':
				return 'center'
			case 'bottom':
				return 'flex-start'
			case 'right':
				return 'center'
			default:
				return 'center'
		}
	}};
	gap: ${({ errorGap = 8 }) => `${errorGap}px`};
`

export const DropzoneTitle = styled.div`
	margin-top: 12px;
	white-space: pre-wrap;
	text-align: center;
`

export const DropzoneSubtitle = styled.div`
	font-size: 12px;
	color: ${themeColor('gray', 5)};
	margin-top: 8px;
	white-space: pre-wrap;
	text-align: center;
`

export const FileError = styled.span`
	font-style: normal;
	font-weight: 400;
	font-size: 12px;
	line-height: 20px;
	color: ${themeColor('pink', 5)};
	max-width: 400px;
	height: fit-content;
`

export const FilePreview = styled(ContentBlockComponent)`
	width: auto;
	height: auto;
	overflow: hidden;

	img {
		display: inline-block;
		width: 100%;
		height: 100%;
		max-width: 360px;
		max-height: 254px;
		object-fit: contain;
		object-position: center;
	}
`

export const FileItemWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	${FilePreview} {
		margin-bottom: 24px;
	}
`

export const FileName = styled.span<{ notView?: boolean }>`
	max-width: calc(100% - 25px);
	white-space: nowrap;
	text-decoration: none;
	line-height: 0;

	${LinkComponentStyled} {
		width: 100%;
		display: inline-block;
		vertical-align: top;
		overflow: hidden;
		text-overflow: ellipsis;
		text-transform: lowercase;
	}

	&:hover {
		cursor: pointer;
	}

	${({ notView }) =>
		notView &&
		css`
			text-decoration: none;
			&:hover {
				cursor: inherit;
			}
		`}
`

export const UploadDropzoneWithoutStyle = styled<FC<DropzoneOptions>>(Dropzone)`
	border: none;
	height: 100%;
	width: ${({ width }) => (width ? '100%' : 'fit-content')};
`
